import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BundlePictureButton } from "@/components/catalogue/BundlePictureButton";

export function AddComponent({ onAdd, taken }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const search = async (v) => {
    setQ(v);
    if (v.trim().length < 2) { setHits([]); return; }
    const r = await api.get("/products", { params: { q: v, group: "Accessories", product_type: "single", page_size: 8 } });
    setHits((r.data.items || []).filter((p) => !taken.includes(p.legacy_id)));
  };
  return (
    <div className="relative mt-2">
      <input className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm" placeholder="Add an Accessories product: search by name…" value={q} onChange={(e) => search(e.target.value)} data-testid="bundle-fix-search" />
      {hits.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow" data-testid="bundle-fix-results">
          {hits.map((p) => (
            <button key={p.legacy_id} type="button" className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50" onClick={() => { onAdd(p); setQ(""); setHits([]); }} data-testid={`bundle-fix-add-${p.legacy_id}`}>
              #{p.legacy_id} {p.title} <span className="text-slate-400">{p.sku}</span>
            </button>))}
        </div>)}
    </div>
  );
}

function FixEditor({ product, onDone }) {
  const b = product.bundle;
  const [rows, setRows] = useState((b.components || []).map((c) => ({ product_legacy_id: c.product_legacy_id, title: c.title, qty: c.qty || "" })));
  const [price, setPrice] = useState(product.selling_price || "");
  const [title, setTitle] = useState(product.title || "");
  const [busy, setBusy] = useState(false);
  const set = (i, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, qty: v } : x)));
  const save = async () => {
    if (!window.confirm("Save these components? The bundle will become sellable and its stock will come from these products.")) return;
    setBusy(true);
    try {
      const r = await api.put(`/new-catalogue/bundles/${product.legacy_id}/components`, { confirm: true, title: title.trim() || null, selling_price: Number(price) || null, components: rows.map((r) => ({ product_legacy_id: r.product_legacy_id, qty: Number(r.qty) || 0 })) });
      const w = r.data.website || {};
      if (w.status === "ok") toast.success("Bundle saved in TedOps and updated on the website");
      else if (w.status === "failed") toast.error(`Saved in TedOps, but the website update failed: ${w.error}`);
      else toast.success("Bundle saved in TedOps (website not changed — only the test bundle is sent for now)");
      onDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="bundle-fix-editor">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Component</th><th>Qty per bundle</th><th /></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.product_legacy_id} className="border-t border-slate-100">
            <td className="py-1.5">#{r.product_legacy_id} {r.title}</td>
            <td><input type="number" min="1" className="h-8 w-20 rounded border border-slate-200 px-2" value={r.qty} onChange={(e) => set(i, e.target.value)} data-testid={`bundle-fix-qty-${r.product_legacy_id}`} /></td>
            <td><button type="button" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} data-testid={`bundle-fix-remove-${r.product_legacy_id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button></td>
          </tr>))}</tbody>
      </table>
      <AddComponent taken={rows.map((r) => r.product_legacy_id)} onAdd={(p) => setRows((x) => [...x, { product_legacy_id: p.legacy_id, title: p.title, qty: 1 }])} />
      <label className="mt-3 flex items-center gap-2 text-sm">Name<input className="h-8 flex-1 rounded border border-slate-200 px-2" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="bundle-fix-title" /></label>
      <label className="mt-3 flex items-center gap-2 text-sm">Selling price £<input type="number" min="0" step="0.01" className="h-8 w-28 rounded border border-slate-200 px-2" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="bundle-fix-price" /></label>
      <div className="mt-3 flex gap-2">
        <Button size="sm" disabled={busy || !rows.length} onClick={save} data-testid="bundle-fix-save">Save and make sellable</Button>
        <Button size="sm" variant="outline" onClick={() => onDone(false)} data-testid="bundle-fix-cancel">Cancel</Button>
      </div>
    </div>
  );
}

export function BundleComponentsCard({ product, onSaved }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const b = product?.bundle;
  if (product?.product_type !== "bundle" || !b) return null;
  const isAdmin = user?.is_super || (user?.roles || []).includes("admin");
  return (
    <div className="ted-card p-5" data-testid="product-bundle-card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Layers className="w-4 h-4 text-violet-500" /> Bundle contents</h2>
        {isAdmin && !editing && <div className="flex flex-wrap gap-2"><BundlePictureButton product={product} onSaved={onSaved} /><Button size="sm" variant={b.activation_blocked ? "default" : "outline"} onClick={() => setEditing(true)} data-testid="bundle-fix-btn"><Pencil className="w-4 h-4" /> {b.activation_blocked ? "Fix bundle" : "Edit components"}</Button></div>}
      </div>
      {b.activation_blocked && (
        <div className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="product-bundle-blocked">
          Blocked from sale until fixed: {(b.block_reasons || []).join("; ")}
        </div>
      )}
      {b.manual_fix && !editing && <p className="mb-2 text-xs text-emerald-700" data-testid="bundle-fixed-note">Corrected in TedOps by {b.fixed_by}; website re-imports keep these components.</p>}
      {b.web_sync && !editing && <p className={`mb-2 text-xs ${b.web_sync.status === "ok" ? "text-emerald-700" : "text-rose-700"}`} data-testid="bundle-web-sync">Website: {b.web_sync.status === "ok" ? `updated ${String(b.web_sync.at).slice(0, 16).replace("T", " ")} (website bundle #${b.web_sync.website_id}${b.web_sync.price ? `, £${b.web_sync.price}` : ""})` : `last update failed — ${b.web_sync.error || b.web_sync.status}`}</p>}
      {editing ? <FixEditor product={product} onDone={(saved) => { setEditing(false); if (saved && onSaved) onSaved(); }} /> : (
        <>
          <p className="text-xs text-slate-500 mb-2">{b.prepacked ? "Pre-packed: this bundle has its own stock." : "No stock of its own — each sale uses the component products below, counted once."}</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Component</th><th>Website ID</th><th>Qty per bundle</th></tr></thead>
            <tbody>{(b.components || []).map((c) => (
              <tr key={c.product_legacy_id} className="border-t border-slate-100" data-testid={`product-bundle-comp-${c.product_legacy_id}`}>
                <td className="py-1.5"><Link to={`/products/${c.product_legacy_id}`} className="text-indigo-700 hover:underline">{c.title || `#${c.product_legacy_id}`}</Link></td>
                <td className="text-slate-500">{c.source_wc_id}</td>
                <td>{c.customer_selectable ? `customer chooses (min ${c.min_quantity || "–"}, max ${c.max_quantity || "–"})` : c.qty}</td>
              </tr>))}</tbody>
          </table>
        </>)}
    </div>
  );
}
